import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { User } from 'firebase/auth';
import { onIdTokenChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import {
  orgRoleFromIdTokenClaims,
  type OrgRole,
} from '../services/firebaseAccountLookup';
import FirebaseAnalyticsService from '../services/firebaseAnalyticsService';

export type { OrgRole };

export type AuthContextValue = {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  token: string | null;
  /** From ID token custom claims (`orgRole`); defaults to member when absent. */
  orgRole: OrgRole | null;
  /** True when signed in and `orgRole === 'admin'`. */
  isOrgAdmin: boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [orgRole, setOrgRole] = useState<OrgRole | null>(null);
  const lastOrgRoleUidRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);

      if (currentUser) {
        FirebaseAnalyticsService.setUser(currentUser.uid);
        if (lastOrgRoleUidRef.current !== currentUser.uid) {
          setOrgRole(null);
          lastOrgRoleUidRef.current = currentUser.uid;
        }
        try {
          const idToken = await currentUser.getIdToken();
          setToken(idToken);
          const { claims } = await currentUser.getIdTokenResult();
          if (auth.currentUser?.uid !== currentUser.uid) return;
          const role = orgRoleFromIdTokenClaims(
            claims as Record<string, unknown>,
          );
          setOrgRole(role);
          FirebaseAnalyticsService.setUserContext({ org_role: role });
        } catch (error: unknown) {
          const code = (error as { code?: string })?.code;
          if (code === 'auth/id-token-expired') {
            window.location.reload();
          }
          if (auth.currentUser?.uid === currentUser.uid) {
            setOrgRole('member');
            FirebaseAnalyticsService.setUserContext({ org_role: 'member' });
          }
        }
      } else {
        lastOrgRoleUidRef.current = null;
        setToken(null);
        setOrgRole(null);
        FirebaseAnalyticsService.clearUser();
      }
    });

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    FirebaseAnalyticsService.trackFeatureUsage('Auth', 'logout');
    FirebaseAnalyticsService.clearUser();
    await signOut(auth);
  };

  const isOrgAdmin = Boolean(user && orgRole === 'admin');

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        logout,
        token,
        orgRole: user ? orgRole : null,
        isOrgAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthContextValue | undefined => {
  return useContext(AuthContext);
};
